import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import toastr from "toastr";
import {
  makeDELETERequest,
  makeDELETERequestOnUserServiceWithQueryParameter,
  makeGETRequest,
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
  makePUTRequestOnUserService,
} from "../Api/api";
import moment from "moment";
import { IUser } from "../components/YieldCalculator/Interfaces";
import Listbox from "../components/Listbox";

interface Coupon {
  isin: string;
  name_of_security: string;
  discount_sell_price?: string;
  discount_sell_yield?: string;
  code?: string;
  expiry_date: string;
  uuid: string;
}

interface SecurityMaster {
  isin: string;
  securityName: string;
}

interface UserCouponList {
  uuid: string;
  coupon: Coupon;
  inserted_at: string;
  updated_at: string;
}

const Anniversary: React.FC = () => {
  const [data, setData] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterIsin, setFilterIsin] = useState("");
  const [filterName, setFilterName] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [secMasters, setSecMasters] = useState<SecurityMaster[]>([]);
  const [searchSec, setSearchSec] = useState("");
  const [newIsin, setNewIsin] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newYield, setNewYield] = useState("");
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const [expiryDateError, setExpiryDateError] = useState(""); 

  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<Coupon | null>(null);
  const [editPrice, setEditPrice] = useState("");
  const [editYield, setEditYield] = useState("");

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [users, setUsers] = useState<{ label: string; id: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<{
    label: string;
    id: string;
  } | null>(null);
  const [userCoupons, setUserCoupons] = useState<UserCouponList[]>([]);
  const [couponToAssign, setCouponToAssign] = useState<string>("");

  const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");

  const validateExpiryDate = (date: string): string => {
    if (!date) return "Expiry date is required";
    const selected = moment(date, "YYYY-MM-DD");
    const today = moment().startOf("day");
    if (!selected.isValid()) return "Invalid date format";
    if (selected.isSameOrBefore(today)) {
      return "Expiry date must be after today";
    }
    return "";
  };

  const handleExpiryDateChange = (value: string) => {
    setNewExpiryDate(value);
    if (value) {
      setExpiryDateError(validateExpiryDate(value));
    } else {
      setExpiryDateError("Expiry date is required");
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const resp = await makeGETRequestOnUserService("/coupon/list/");
      setData(resp.data);
    } catch (err: any) {
      toastr.error(
        "API failed: " + (err.response?.data?.detail || err.message)
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  useEffect(() => {
    if (addOpen) {
      makeGETRequest("/admin/security_master/list/formatted/")
        .then((res) => setSecMasters(res.data))
        .catch(() => toastr.error("Failed to fetch security master list"));
    }
  }, [addOpen]);

  const fetchUsers = () => {
    makeGETRequestOnUserService("/user/user_list/")
      .then((response) => {
        if (response?.status === 200) {
          const usersArray = (response.data as IUser[]).map((item) => ({
            label: `${item.firstName} ${item.lastName} - ${item.mobileNumber}`,
            id: item.uuid,
          }));
          setUsers(usersArray);
        }
      })
      .catch((error) => toastr.error(error));
  };

  const fetchUserCoupons = (userId: string) => {
    makeGETRequestOnUserService(`/coupon/user/list/${userId}/`)
      .then((resp) => {
        setUserCoupons(resp.data);
      })
      .catch((err: any) => {
        if (err.response?.data?.detail === "This user has no coupons") {
          setUserCoupons([]);
        } else {
          toastr.error("Failed to fetch user coupons: " + err.message);
        }
      });
  };

  const filtered = useMemo(
    () =>
      data.filter(
        (d) =>
          (filterIsin ? d.isin.includes(filterIsin) : true) &&
          (filterName
            ? d.name_of_security
                .toLowerCase()
                .includes(filterName.toLowerCase())
            : true)
      ),
    [data, filterIsin, filterName]
  );

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const handleAdd = async () => {
    const error = validateExpiryDate(newExpiryDate);
    if (error) {
      setExpiryDateError(error);
      return;
    }

    try {
      await makePOSTRequestOnUserService("/coupon/create/", {
        isin: newIsin,
        discount_sell_price: newPrice,
        discount_sell_yield: newYield,
        code: Math.random().toString(36).substring(2, 12),
        expiry_date: newExpiryDate,
      });
      toastr.success("Coupon added");
      setAddOpen(false);
      setNewIsin("");
      setNewPrice("");
      setNewYield("");
      setNewExpiryDate("");
      setExpiryDateError("");
      setSearchSec("");
      fetchList();
    } catch (err: any) {
      toastr.error(
        "Add failed: " + (err.response?.data?.detail || err.message)
      );
    }
  };

  const handleEdit = (rec: Coupon) => {
    setEditRecord(rec);
    setEditPrice(rec.discount_sell_price || "");
    setEditYield(rec.discount_sell_yield || "");
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editRecord) return;
    try {
      await makePUTRequestOnUserService(`/coupon/update/${editRecord.uuid}/`, {
        isin: editRecord.isin,
        discount_sell_price: editPrice,
        discount_sell_yield: editYield,
      });
      toastr.success("Updated");
      setEditOpen(false);
      fetchList();
    } catch (err: any) {
      toastr.error(
        "Update failed: " + (err.response?.data?.detail || err.message)
      );
    }
  };

  const handleDelete = async (uuid: string) => {
    try {
      await makeDELETERequestOnUserServiceWithQueryParameter(
        `coupon/delete/${uuid}/`
      );
      toastr.success("Deleted");
      fetchList();
    } catch (err: any) {
      toastr.error(
        "Delete failed: " + (err.response?.data?.detail || err.message)
      );
    }
  };

  const handleRemoveUserCoupon = async (relUuid: string) => {
    try {
      await makeDELETERequestOnUserServiceWithQueryParameter(
        `/coupon/user_relationship_delete/${relUuid}/`
      );
      toastr.success("Coupon removed from user");
      if (selectedUser) fetchUserCoupons(selectedUser.id);
    } catch (err: any) {
      toastr.error(
        "Remove failed: " + (err.response?.data?.detail || err.message)
      );
    }
  };

  const handleAssignCoupon = async () => {
    if (!selectedUser || !couponToAssign) return;
    try {
      await makePOSTRequestOnUserService("/coupon/user_relationship_create/", {
        user_uuid: selectedUser.id,
        coupon_uuid: couponToAssign,
      });
      toastr.success("Coupon assigned to user");
      setCouponToAssign("");
      fetchUserCoupons(selectedUser.id);
    } catch (err: any) {
      toastr.error(
        "Assign failed: " + (err.response?.data?.detail || err.message)
      );
    }
  };

  const filteredSec = useMemo(
    () =>
      secMasters
        .filter(
          (s) =>
            s.isin.includes(searchSec) ||
            s.securityName.toLowerCase().includes(searchSec.toLowerCase())
        )
        .slice(0, 100),
    [secMasters, searchSec]
  );

  const isAddDisabled = !newIsin || !newExpiryDate || expiryDateError !== "";

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box mx="auto" my={2} maxWidth="90%">
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Filter by ISIN"
          value={filterIsin}
          onChange={(e) => setFilterIsin(e.target.value)}
          size="small"
        />
        <TextField
          label="Filter by Security Name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          size="small"
        />
        <Button
          variant="outlined"
          sx={{ px: 2, py: 0 }}
          startIcon={<Add />}
          size="small"
          disableElevation
          onClick={() => setAddOpen(true)}
        >
          Add
        </Button>
        <Button
          variant="outlined"
          startIcon={<Add />}
          size="small"
          onClick={() => {
            setUserDialogOpen(true);
            fetchUsers();
            setSelectedUser(null);
            setUserCoupons([]);
          }}
        >
          User Actions
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>ISIN</TableCell>
              <TableCell>Security Name</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Yield</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((row) => (
              <TableRow key={row.uuid}>
                <TableCell>{row.isin}</TableCell>
                <TableCell>{row.name_of_security}</TableCell>
                <TableCell>{row.discount_sell_price}</TableCell>
                <TableCell>{row.discount_sell_yield}</TableCell>
                <TableCell>
                  {row.expiry_date
                    ? moment(row.expiry_date).format("DD/MM/YYYY")
                    : "-"}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(row)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(row.uuid)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, { value: -1, label: "All" }]}
      />

      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add Coupon</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
            overflow: "visible",
          }}
        >
          <TextField
            label="Search Security"
            value={searchSec}
            onChange={(e) => setSearchSec(e.target.value)}
            size="small"
            placeholder="Type ISIN or name"
          />
          <Select
            value={newIsin}
            onChange={(e) => setNewIsin(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Security
            </MenuItem>
            {filteredSec.map((s) => (
              <MenuItem key={s.isin} value={s.isin}>
                {`${s.isin} - ${s.securityName}`}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label="Discount Sell Price"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            type="number"
          />
          <TextField
            label="Discount Sell Yield"
            value={newYield}
            onChange={(e) => setNewYield(e.target.value)}
            type="number"
          />
          <TextField
            label="Expiry Date"
            type="date"
            value={newExpiryDate}
            onChange={(e) => handleExpiryDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: tomorrow }} 
            error={!!expiryDateError}  
            helperText={expiryDateError} 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={isAddDisabled}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Coupon</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <TextField
            label="Discount Sell Price"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            type="number"
          />
          <TextField
            label="Discount Sell Yield"
            value={editYield}
            onChange={(e) => setEditYield(e.target.value)}
            type="number"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        fullWidth
      >
        <DialogTitle>User Actions</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <Autocomplete
            disablePortal
            ListboxComponent={Listbox}
            options={users}
            getOptionLabel={(opt) => opt.label}
            value={selectedUser}
            onChange={(_, newVal) => {
              setSelectedUser(newVal);
              if (newVal) fetchUserCoupons(newVal.id);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select User" />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label}
              </li>
            )}
          />

          {selectedUser && (
            <Box>
              <Typography variant="subtitle1">Assigned Coupons:</Typography>
              {userCoupons.length > 0 ? (
                userCoupons.map((uc) => (
                  <Box
                    key={uc.uuid}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography>
                      {uc.coupon.isin} - {uc.coupon.name_of_security}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveUserCoupon(uc.uuid)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))
              ) : (
                <Typography>No coupons assigned</Typography>
              )}

              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Assign New Coupon:
              </Typography>
              <TextField
                select
                fullWidth
                value={couponToAssign}
                onChange={(e) => setCouponToAssign(e.target.value)}
                label="Coupon"
                size="small"
              >
                <MenuItem value="" disabled>
                  Select Coupon
                </MenuItem>
                {data.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.isin} - {c.name_of_security}
                  </MenuItem>
                ))}
              </TextField>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button variant="contained" onClick={handleAssignCoupon}>
                  Assign
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Anniversary;